import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import axios from 'axios';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import ld from '../../images/load.gif';
import 'react-toastify/dist/ReactToastify.css';
import { ToastContainer, toast } from 'react-toastify';
import { PayPalButton } from 'react-paypal-button-v2';
import styles from '../../styles/order.module.css';

const STATUS_FLOW = ['pending', 'confirmed', 'processing', 'shipped', 'delivered'];

function Order() {
  const { users } = useSelector((state) => state);
  const [data, setData] = useState(null);
  const { id } = useParams();
  const [sdk, setSdk] = useState(false);
  const navigate = useNavigate();

  const getData = async () => {
    try {
      const res = await axios.get(`https://pro-shop-backend.vercel.app/api/orders/${id}`);
      const order = res.data.message;
      setData(order);

      if (order?.user?._id !== users.userdata._id && users.userdata.isAdmin !== true) {
        toast.error('You are not authorized to view this order', {
          position: 'bottom-right',
          theme: 'colored'
        });
        navigate('/');
        return;
      }

      if (order?.isPaid !== true) {
        if (!window.paypal) {
          addPaypalScript();
        } else {
          setSdk(true);
        }
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch order details';
      toast.error(errorMessage, {
        position: 'bottom-right',
        theme: 'colored'
      });
      navigate('/');
    }
  };

  const addPaypalScript = async () => {
    try {
      const { data: clientId } = await axios.get('https://pro-shop-backend.vercel.app/api/config/paypal');
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = `https://www.paypal.com/sdk/js?client-id=${clientId}`;
      script.async = true;
      script.onload = () => {
        setSdk(true);
      };
      document.body.appendChild(script);
    } catch (error) {
      toast.error('Failed to load PayPal. Please try again.', {
        position: 'bottom-right',
        theme: 'colored'
      });
    }
  };

  useEffect(() => {
    getData();
  }, [id]);

  const updateStatusHandler = async (status) => {
    try {
      const res = await axios.put(`https://pro-shop-backend.vercel.app/api/orders/${data._id}/status`, { status });
      if (res.data.success === true) {
        toast.success(res.data.message || `Order marked as ${status}`, {
          position: 'bottom-right',
          theme: 'colored'
        });
        getData();
      } else {
        toast.error(res.data.message || 'Failed to update order status', {
          position: 'bottom-right',
          theme: 'colored'
        });
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || 'Failed to update order status';
      toast.error(errorMessage, {
        position: 'bottom-right',
        theme: 'colored'
      });
    }
  };

  const paymentSuccessHandler = async (paymentResult) => {
    try {
      const res = await axios.put(`https://pro-shop-backend.vercel.app/api/orders/${data._id}/pay`, paymentResult);
      if (res.data.success === true) {
        toast.success(res.data.message || 'Payment successful!', {
          position: 'bottom-right',
          theme: 'colored'
        });
        getData();
      } else {
        toast.error(res.data.message || 'Payment failed', {
          position: 'bottom-right',
          theme: 'colored'
        });
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Payment failed. Please try again.';
      toast.error(errorMessage, {
        position: 'bottom-right',
        theme: 'colored'
      });
    }
  };

  const currentIndex = data ? STATUS_FLOW.indexOf(data.status) : -1;
  const nextStatus =
    currentIndex >= 0 && currentIndex < STATUS_FLOW.length - 1
      ? STATUS_FLOW[currentIndex + 1]
      : null;

  const getStatusColor = (status) => {
    switch(status) {
      case 'confirmed':
        return styles.statusConfirmed;
      case 'shipped':
        return styles.statusShipped;
      case 'delivered':
        return styles.statusDelivered;
      case 'cancelled':
        return styles.statusCancelled;
      default:
        return styles.statusPending;
    }
  };

  const getStatusBadgeColor = (status) => {
    switch(status) {
      case 'confirmed':
        return styles.badgeConfirmed;
      case 'shipped':
        return styles.badgeShipped;
      case 'delivered':
        return styles.badgeDelivered;
      case 'cancelled':
        return styles.badgeCancelled;
      default:
        return styles.badgePending;
    }
  };

  if (!data) {
    return (
      <div className={styles.orderContainer}>
        <Navbar />
        <div className={styles.loadingContainer}>
          <img src={ld} className={styles.loaderImage} alt="Loading..." />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.orderContainer}>
      <Navbar />
      
      <div className={styles.orderWrapper}>
        <div className={styles.orderHeader}>
          <h2 className={styles.orderId}>Order ID: <span>{id}</span></h2>
          <span className={`${styles.statusBadge} ${getStatusBadgeColor(data.status)}`}>
            {data.status || 'Pending'}
          </span>
        </div>

        <div className={styles.orderGrid}>
          <div className={styles.orderDetails}>
            <div className={styles.detailSection}>
              <h4 className={styles.sectionTitle}>Shipping Details</h4>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Address:</span>
                <span>{data.shippingAddress.address}, {data.shippingAddress.city}, {data.shippingAddress.postalCode}, {data.shippingAddress.country}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Customer:</span>
                <span>{data.user.name}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Email:</span>
                <span>{data.user.email}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Phone:</span>
                <span>{data.mobileNumber}</span>
              </div>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Status:</span>
                <span className={`${styles.statusText} ${getStatusColor(data.status)}`}>
                  {data.status || 'Pending'}
                </span>
              </div>
            </div>

            <div className={styles.detailSection}>
              <h4 className={styles.sectionTitle}>Payment Details</h4>
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>Status:</span>
                <span className={data.isPaid ? styles.paidText : styles.unpaidText}>
                  {data.isPaid ? 'PAID' : 'NOT PAID'}
                </span>
              </div>
              {data.isPaid === false && (
                <div className={styles.paymentNotice}>
                  <span>You can pay on delivery or pay now with PayPal</span>
                </div>
              )}
            </div>

            <div className={styles.detailSection}>
              <h4 className={styles.sectionTitle}>Order Items</h4>
              {data.orderItems.map((elem, i) => (
                <div key={i} className={styles.orderItem}>
                  <img src={elem.image} alt={elem.name} className={styles.orderItemImage} />
                  <div className={styles.orderItemDetails}>
                    <span className={styles.orderItemName}>{elem.name}</span>
                    <span className={styles.orderItemPrice}>
                      {elem.quantity} × ${elem.price} = ${(elem.quantity * elem.price)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className={styles.orderSummary}>
            <h4 className={styles.summaryTitle}>Order Summary</h4>
            
            <div className={styles.summaryRow}>
              <span>Items</span>
              <span>${(data.totalPrice - data.shippingPrice)}</span>
            </div>
            
            <div className={styles.summaryRow}>
              <span>Shipping</span>
              <span>${data.shippingPrice}</span>
            </div>
            
            <div className={`${styles.summaryRow} ${styles.summaryTotal}`}>
              <span>Total</span>
              <span>${data.totalPrice}</span>
            </div>

            {data.isPaid === false && data.user._id === users.userdata._id && sdk === true && (
              <div className={styles.paypalWrapper}>
                <PayPalButton 
                  amount={data.totalPrice} 
                  onSuccess={paymentSuccessHandler} 
                />
              </div>
            )}

            {users.userdata.isAdmin === true && (
              <div className={styles.adminControls}>
                {nextStatus && (
                  <button
                    type="button"
                    className={styles.statusUpdateBtn}
                    onClick={() => updateStatusHandler(nextStatus)}
                  >
                    Mark as {nextStatus}
                  </button>
                )}
                {data.status !== 'cancelled' && data.status !== 'delivered' && (
                  <button
                    type="button"
                    className={`${styles.statusUpdateBtn} ${styles.cancelBtn}`}
                    onClick={() => updateStatusHandler('cancelled')}
                  >
                    Cancel Order
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <ToastContainer
        position="bottom-right"
        theme="colored"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
}

export default Order;